/* eslint-disable react/no-array-index-key */
/* eslint-disable max-lines */
/* eslint-disable complexity */
import React, { useRef } from 'react';
import { describe } from '@elliemae/ds-props-helpers';
import {
  styled,
  space,
  color,
  border,
  op,
  truncate,
  withTheme,
  __UNSAFE_SPACE_TO_DIMSUM,
  toMobile,
} from '@elliemae/ds-system';
import { GroupItem } from '@elliemae/ds-shared';
import { Grid } from '@elliemae/ds-grid';
import { StyledCard as Card } from './StyledCard.js';
import { ExpandChevron } from './ExpandChevron.js';
import { useHeight } from './useHeight.js';
import { ExpandableRegion } from './ExpandableRegion.js';
import type { MobileCardProps } from './props.js';
import { cardPropsTypes } from './props.js';

const TitleEllipsis = styled.div`
  ${truncate()}
`;

const Title = styled.div`
  ${space}
  ${truncate()}
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  font-size: ${(props) => toMobile(props.theme.fontSizes.title[600])};
  width: 100%;
  ${color('neutral', 800)}
`;
const Detail = styled.span`
  ${space}
  ${truncate()}
  font-size: ${(props) => toMobile(props.theme.fontSizes.title[500])};
  font-weight: ${(props) => props.theme.fontWeights.regular};
`;
const SubText = styled.span`
  margin-top: ${(props) => __UNSAFE_SPACE_TO_DIMSUM(props.theme.space.xs)};
  ${truncate()}
  &:nth-child(2) {
    padding-left: ${(props) => __UNSAFE_SPACE_TO_DIMSUM(props.theme.space.s)};
  }
`;
const SubTextWrap = styled.div`
  ${truncate()}
  border-top: ${(props) => border(props.theme.colors.neutral['300'])};
  margin-top: 6px;
  margin-bottom: 2px;
  padding-bottom: ${(props) => __UNSAFE_SPACE_TO_DIMSUM(props.theme.space.xxs)};
  font-size: ${(props) => toMobile(props.theme.fontSizes.subTitle[400])};
  ${color('neutral', 500)}
  font-weight: ${(props) => props.theme.fontWeights.regular};
  display: flex;
  justify-content: space-between;
`;
const LeftIcon = styled(Grid)<{ halfM: string }>`
  & > span {
    height: ${(props) => props.halfM};
    width: ${(props) => props.halfM};
  }

  & > span > svg,
  svg:not([fill]) {
    height: ${(props) => props.halfM};
    width: ${(props) => props.halfM};
  }
`;

const RightIcon = styled(Grid)<{ halfM: string }>`
  span > svg,
  span > svg:not([fill]) {
    fill: #000000;
  }
`;
const CenterWrap = styled.div`
  display: flex;
  align-items: center;
  ${truncate()}
`;

const MobileCard: React.ComponentType<MobileCardProps> = ({
  children,
  height = 'm',
  leftAddOn,
  leftProp,
  rightProp,
  subText,
  subIcons,
  details,
  theme,
  expandable = false,
  expandedContent,
  value,
  cardContent,
}) => {
  const subTextRef = useRef(null);
  const subTextHeight = useHeight(subTextRef);
  const isJustChildren = !(subText || subIcons || details);
  const halfM = op('/', __UNSAFE_SPACE_TO_DIMSUM(theme.space.m), 2);

  return (
    <GroupItem
      render={(context = {}) => {
        const { activeValue, onChange } = context;
        const selected =
          activeValue !== undefined && (activeValue === value || (activeValue?.length && activeValue?.includes(value)));

        return (
          <Card
            data-testid="card-wrapper"
            minHeight={
              height === 'm' ? __UNSAFE_SPACE_TO_DIMSUM(theme.space.xxl) : __UNSAFE_SPACE_TO_DIMSUM(theme.space.xl)
            }
          >
            {expandable && (
              <ExpandChevron
                isExpanded={selected}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ target: { value } });
                }}
                align={subText || subIcons ? 'top' : 'center'}
              />
            )}
            <Grid pb={(subText || subIcons) && subTextHeight} cols={['auto', 'auto']} alignItems="center">
              {leftProp && <Grid ml={__UNSAFE_SPACE_TO_DIMSUM(theme.space.s)}>{leftProp}</Grid>}
              {leftAddOn && (
                <LeftIcon ml={__UNSAFE_SPACE_TO_DIMSUM(theme.space.s)} halfM={halfM}>
                  {leftAddOn}
                </LeftIcon>
              )}
            </Grid>
            <Grid
              pl={!leftAddOn ? __UNSAFE_SPACE_TO_DIMSUM(theme.space.s) : __UNSAFE_SPACE_TO_DIMSUM(theme.space.xs)}
              pr={__UNSAFE_SPACE_TO_DIMSUM(theme.space.s)}
              width="100%"
            >
              <CenterWrap>
                <Title pt={!isJustChildren && halfM} data-testid="card-title">
                  <TitleEllipsis>{children}</TitleEllipsis>
                  {details && (
                    <Grid
                      data-testid="card-details"
                      cols={details.map(() => 'min-content')}
                      mb={(!(subText || subIcons) && halfM) || 0}
                      pt={op('/', __UNSAFE_SPACE_TO_DIMSUM(theme.space.xxs), 2)}
                    >
                      {details.map((d, i) => (
                        <Detail key={i}>
                          {d}
                          {i < details.length - 1 && <Detail px={__UNSAFE_SPACE_TO_DIMSUM(theme.space.xxs)}>•</Detail>}
                        </Detail>
                      ))}
                    </Grid>
                  )}
                </Title>
                {rightProp &&
                  rightProp.map((prop, i) => (
                    <RightIcon ml={__UNSAFE_SPACE_TO_DIMSUM(theme.space.s)} key={i}>
                      {prop}
                    </RightIcon>
                  ))}
              </CenterWrap>
              {subIcons && (
                <SubTextWrap innerRef={subTextRef}>
                  <SubText>{subIcons}</SubText>
                  {subText && subText[1] && <SubText data-testid="card-subText">{subText[1]}</SubText>}
                </SubTextWrap>
              )}
              {subText && !subIcons && (
                <SubTextWrap innerRef={subTextRef}>
                  {subText.map((s, i) => (
                    <SubText data-testid="card-subText" key={i}>
                      {s}
                    </SubText>
                  ))}
                </SubTextWrap>
              )}
              <ExpandableRegion show={selected} content={expandedContent} />
              {cardContent && <Grid>{cardContent}</Grid>}
            </Grid>
          </Card>
        );
      }}
    />
  );
};

MobileCard.displayName = 'MobileCard';
const DSMobileCardWithSchema = describe(MobileCard);

DSMobileCardWithSchema.propTypes = cardPropsTypes;

export { DSMobileCardWithSchema };

export default withTheme(MobileCard);
